import axios from "axios";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;

function getHeaders() {
  return {
    accessToken: localStorage.getItem("accessToken"),
  };
}

function buildConfig({ params, data } = {}) {
  const config = {
    headers: getHeaders(),
  };
  if (params) config.params = params;
  if (data) config.data = data;
  return config;
}

function unwrap(response) {
  return response?.data || {};
}

function normalizeInstanceParams(instanceInput) {
  if (instanceInput == null || instanceInput === "") return {};
  if (typeof instanceInput === "object" && !Array.isArray(instanceInput)) return instanceInput;
  return { instanceUniqueId: instanceInput };
}

async function get(url, params) {
  return unwrap(await axios.get(`${BASE_URL}${url}`, buildConfig({ params })));
}

async function getBlob(url, params) {
  const response = await axios.get(`${BASE_URL}${url}`, {
    ...buildConfig({ params }),
    responseType: "blob",
  });
  return response.data;
}

async function post(url, body, params) {
  return unwrap(await axios.post(`${BASE_URL}${url}`, body || {}, buildConfig({ params })));
}

async function patch(url, body, params) {
  return unwrap(await axios.patch(`${BASE_URL}${url}`, body || {}, buildConfig({ params })));
}

export async function listCoreNetworkMembers(filters = {}) {
  return get("/core-network/members", filters);
}

export async function getCoreNetworkMember(memberId, instanceInput) {
  return get(`/core-network/members/${memberId}`, normalizeInstanceParams(instanceInput));
}

export async function createCoreNetworkMember(data = {}) {
  return post("/core-network/members", data);
}

export async function onboardArtworkDesignerTalent(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/members/onboard-artwork-designer", data, params);
}

export async function bulkImportCoreNetworkMembers(rows = []) {
  return post("/core-network/members/bulk-import", { rows });
}

export async function updateCoreNetworkMember(memberId, data = {}) {
  return patch(`/core-network/members/${memberId}`, data);
}

export async function sendCoreNetworkPortalInvite(memberId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/members/${memberId}/portal-invite`, data, params);
}

export async function updateCoreNetworkPortalAccess(memberId, data = {}) {
  return patch(`/core-network/members/${memberId}/portal-access`, data);
}

export async function sendTalentPasswordReset(memberId, data = {}) {
  return post(`/api/platform/core-network/members/${memberId}/send-password-reset`, data);
}

export async function generateTalentTemporaryPassword(memberId, data = {}) {
  return post(`/api/platform/core-network/members/${memberId}/generate-temporary-password`, data);
}

export async function setTalentTemporaryPassword(memberId, data = {}) {
  return post(`/api/platform/core-network/members/${memberId}/set-temporary-password`, data);
}

export async function listCoreNetworkBadges(filters = {}) {
  return get("/core-network/badges", filters);
}

export async function createCoreNetworkBadge(data = {}) {
  return post("/core-network/badges", data);
}

export async function updateCoreNetworkBadge(badgeId, data = {}) {
  return patch(`/core-network/badges/${badgeId}`, data);
}

export async function listCoreNetworkMemberBadges(filters = {}) {
  return get("/core-network/member-badges", filters);
}

export async function assignCoreNetworkBadge(memberId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/members/${memberId}/badges`, data, params);
}

export async function revokeCoreNetworkBadge(memberBadgeId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/member-badges/${memberBadgeId}/revoke`, data, params);
}

export async function bulkAssignCoreNetworkBadges(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/member-badges/bulk-assign", { rows: data.rows || [] }, params);
}

export async function listCoreNetworkMemberships(filters = {}) {
  return get("/core-network/memberships", filters);
}

export async function createCoreNetworkMembership(memberId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/members/${memberId}/memberships`, data, params);
}

export async function bulkAssignCoreNetworkMemberships(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/memberships/bulk-assign", { rows: data.rows || [] }, params);
}

export async function updateCoreNetworkMembership(membershipId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch(`/core-network/memberships/${membershipId}`, data, params);
}

export async function listCoreNetworkSubscriptions(filters = {}) {
  return get("/core-network/subscriptions", filters);
}

export async function createCoreNetworkSubscription(memberId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/members/${memberId}/subscriptions`, data, params);
}

export async function updateCoreNetworkSubscription(subscriptionId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch(`/core-network/subscriptions/${subscriptionId}`, data, params);
}

export async function listCoreNetworkCapabilities(filters = {}) {
  return get("/core-network/capabilities", filters);
}

export async function createCoreNetworkCapability(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/capabilities", data, params);
}

export async function bulkAddCoreNetworkCapabilities(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/capabilities/bulk", { rows: data.rows || [] }, params);
}

export async function updateCoreNetworkCapability(capabilityId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch(`/core-network/capabilities/${capabilityId}`, data, params);
}

export async function listCoreNetworkCapacity(filters = {}) {
  return get("/core-network/capacity", filters);
}

export async function upsertCoreNetworkCapacity(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/capacity", data, params);
}

export async function bulkAddCoreNetworkCapacity(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/capacity/bulk", { rows: data.rows || [] }, params);
}

export async function getCoreNetworkAdminSettings(instanceInput) {
  return get("/core-network/admin-settings", normalizeInstanceParams(instanceInput));
}

export async function getCoreNetworkAccessProfile(instanceInput) {
  return get("/core-network/access-profile", normalizeInstanceParams(instanceInput));
}

export async function updateCoreNetworkAdminSettings(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch("/core-network/admin-settings", data, params);
}

export async function seedCoreNetworkAdminDefaults(instanceInput) {
  return post("/core-network/admin-settings/seed-defaults", {}, normalizeInstanceParams(instanceInput));
}

export async function exportCoreNetworkMembersCsv(instanceInput) {
  return getBlob("/core-network/export/members.csv", normalizeInstanceParams(instanceInput));
}

export async function exportCoreNetworkCapabilitiesCsv(instanceInput) {
  return getBlob("/core-network/export/capabilities.csv", normalizeInstanceParams(instanceInput));
}

export async function exportCoreNetworkCapacityCsv(instanceInput) {
  return getBlob("/core-network/export/capacity.csv", normalizeInstanceParams(instanceInput));
}

export async function listCoreNetworkWorkRequests(filters = {}) {
  return get("/core-network/work-requests", filters);
}

export async function getCoreNetworkArtworkWorkloadReport(filters = {}) {
  return get("/core-network/reporting/artwork-workload", filters);
}

export async function getCoreNetworkWorkRequest(workRequestId, instanceInput) {
  return get(`/core-network/work-requests/${workRequestId}`, normalizeInstanceParams(instanceInput));
}

export async function createCoreNetworkWorkRequest(data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post("/core-network/work-requests", data, params);
}

export async function updateCoreNetworkWorkRequest(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch(`/core-network/work-requests/${workRequestId}`, data, params);
}

export async function cancelCoreNetworkWorkRequest(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/cancel`, data, params);
}

export async function assignCoreNetworkWorkRequest(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/assign`, data, params);
}

export async function createCoreNetworkProductionWorkRequest(productionJobId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/production-jobs/${productionJobId}/create-work-request`, data, params);
}

export async function listCoreNetworkMessages(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/messages`, filters);
}

export async function addCoreNetworkMessage(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/messages`, data, params);
}

export async function uploadCoreNetworkMessageAttachments(workRequestId, files = [], data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  const formData = new FormData();
  safeFileList(files).forEach((file) => {
    formData.append("files", file);
  });
  if (data.assignmentId != null) {
    formData.append("assignmentId", data.assignmentId);
  }
  return unwrap(
    await axios.post(`${BASE_URL}/core-network/work-requests/${workRequestId}/message-attachments`, formData, {
      ...buildConfig({ params }),
      headers: {
        ...getHeaders(),
        "Content-Type": "multipart/form-data",
      },
    })
  );
}

export async function markCoreNetworkMessagesRead(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/messages/mark-read`, data, params);
}

export async function getCoreNetworkMessageAttachment(messageId, fileId, filters = {}) {
  return get(`/core-network/messages/${messageId}/attachments/${fileId}/download`, filters);
}

export async function getCoreNetworkTalentAssignmentPreview(workRequestId, assignmentId, instanceInput) {
  const params = normalizeInstanceParams(instanceInput);
  return get(`/core-network/work-requests/${workRequestId}/talent-preview/${assignmentId}`, params);
}

export async function createCoreNetworkSubmission(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/submissions`, data, params);
}

export async function reviewCoreNetworkSubmission(submissionId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return patch(`/core-network/submissions/${submissionId}/review`, data, params);
}

export async function generateCoreNetworkMatches(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/generate-matches`, data, params);
}

export async function listCoreNetworkMatches(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/matches`, filters);
}

export async function shortlistCoreNetworkMatches(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/shortlist-matches`, data, params);
}

export async function updateCoreNetworkProviderStatus(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/provider-status`, data, params);
}

export async function listCoreNetworkProviderStatusHistory(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/provider-status-history`, filters);
}

export async function sendCoreNetworkOffers(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/offers/send-top-matches`, data, params);
}

export async function createCoreNetworkOffer(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/offers`, data, params);
}

export async function listCoreNetworkOffers(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/offers`, filters);
}

export async function acceptCoreNetworkOffer(offerId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/offers/${offerId}/accept`, data, params);
}

export async function declineCoreNetworkOffer(offerId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/offers/${offerId}/decline`, data, params);
}

export async function cancelCoreNetworkOffer(offerId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/offers/${offerId}/cancel`, data, params);
}

export async function selectCoreNetworkOffer(offerId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/offers/${offerId}/select`, data, params);
}

export async function listCoreNetworkEvents(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/events`, filters);
}

export async function completeCoreNetworkAssignment(assignmentId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/assignments/${assignmentId}/complete`, data, params);
}

export async function listCoreNetworkRatings(workRequestId, filters = {}) {
  return get(`/core-network/work-requests/${workRequestId}/ratings`, filters);
}

export async function createCoreNetworkRating(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/ratings`, data, params);
}

export async function listCoreNetworkMemberRatings(memberId, filters = {}) {
  return get(`/core-network/members/${memberId}/ratings`, filters);
}

export async function getCoreNetworkMemberRatingSummary(memberId, instanceInput) {
  return get(`/core-network/members/${memberId}/rating-summary`, normalizeInstanceParams(instanceInput));
}

export async function listCoreNetworkPayouts(filters = {}) {
  return get("/core-network/payouts", filters);
}

export async function createOrUpdateCoreNetworkPayout(assignmentId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/assignments/${assignmentId}/payout`, data, params);
}

export async function approveCoreNetworkPayout(payoutId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/payouts/${payoutId}/approve`, data, params);
}

export async function markCoreNetworkPayoutPaid(payoutId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/payouts/${payoutId}/mark-paid`, data, params);
}

export async function cancelCoreNetworkPayout(payoutId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/payouts/${payoutId}/cancel`, data, params);
}

export async function listCoreNetworkMarketplaceFees(filters = {}) {
  return get("/core-network/marketplace-fees", filters);
}

export async function createCoreNetworkMarketplaceFee(workRequestId, data = {}) {
  const params = normalizeInstanceParams(data.instanceId || data.instanceUniqueId || data.instanceSlug);
  return post(`/core-network/work-requests/${workRequestId}/marketplace-fees`, data, params);
}

function safeFileList(files) {
  if (!files) return [];
  return Array.isArray(files) ? files.filter(Boolean) : Array.from(files).filter(Boolean);
}
